import {
    FinancialConnection,
    FinancialConnectionWithAccounts,
} from "@common/types/financialConnection";
import { FinancialConnectionDto } from "@common/dto/financialAccount/financial-connection.dto";
import { Entity } from "@common/types/entity";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { integrationConnectionWithAccountsResponseSchema } from "@common/schemas/domains/integrationConnection/integration-connection-response.schema";
import { gustoContract } from "@common/contracts/gusto.contract";
import { gustoIntegrationContract } from "@common/contracts/gusto-integration.contract";
import { backendClient, unwrapResponse } from "./backendClient";
import { getBackendAPIClient } from "./backendAPIClient";

const gustoContractClient = getBackendAPIClient(gustoContract);
const gustoIntegrationContractClient = getBackendAPIClient(
    gustoIntegrationContract,
);

export type GustoConnectionForEntity =
    | IntegrationConnection
    | FinancialConnectionWithAccounts;

export async function getGustoConnectionForEntity(
    entity: Entity,
): Promise<GustoConnectionForEntity | null> {
    // Prefer the new integration_connection flow; fall back to the legacy
    // gusto_connection for connections not yet migrated.
    const newResponse =
        await gustoIntegrationContractClient.getConnectionForEntity({
            params: { entityId: entity.id },
        });
    if (newResponse.body) {
        return integrationConnectionWithAccountsResponseSchema.parse(
            newResponse.body,
        );
    }

    const legacyResponse = unwrapResponse(
        await backendClient.get(`/gusto-connection/entity/${entity.id}`),
    );
    return legacyResponse
        ? FinancialConnectionDto.deserialize(legacyResponse)
        : null;
}

export async function syncGustoConnection(
    connection: GustoConnectionForEntity,
    workspaceId: string,
) {
    // Integer id => legacy gusto_connection, UUID id => integration_connection.
    if (typeof connection.id === "number") {
        const response = await gustoContractClient.syncConnection({
            params: { connectionId: connection.id },
        });
        return FinancialConnectionDto.deserialize(response.body);
    }

    const response = await gustoIntegrationContractClient.syncConnection({
        params: { connectionId: connection.id },
        query: { workspaceId },
    });
    return integrationConnectionWithAccountsResponseSchema.parse(response.body);
}

export async function fixGustoConnection(
    connection: FinancialConnection,
    code: string,
    workspaceId: string,
): Promise<IntegrationConnection> {
    // Fixing a legacy connection migrates it to the new integration_connection
    // flow and returns the newly created integration connection. workspaceId is
    // required so the backend can resolve and authorize the target workspace.
    const response = await gustoContractClient.fixConnection({
        params: { connectionId: connection.id },
        query: { workspaceId },
        body: { code },
    });
    return integrationConnectionWithAccountsResponseSchema.parse(response.body);
}
